import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { ZoomIn, ZoomOut } from 'lucide-react';

// Helper to get cropped image with support for negative and out-of-bounds coordinates (zoomed out)
const getCroppedImg = (
    imageSrc: string, 
    pixelCrop: { x: number; y: number; width: number; height: number },
    aspect: number = 1
): Promise<Blob | null> => {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.setAttribute('crossOrigin', 'anonymous'); // avoid CORS taint if applicable
        image.src = imageSrc;
        image.onload = () => {
            try {
                const roundedX = Math.round(pixelCrop.x);
                const roundedY = Math.round(pixelCrop.y);
                const roundedWidth = Math.max(1, Math.round(pixelCrop.width));
                const roundedHeight = Math.max(1, Math.round(pixelCrop.height));

                // Constrain maximum output dimensions to prevent oversized base64 strings exceeding Firestore's 1MB limit
                const maxDimension = aspect === 1 ? 512 : 1200;
                let outputWidth = roundedWidth;
                let outputHeight = roundedHeight;

                if (outputWidth > maxDimension) {
                    const scale = maxDimension / outputWidth;
                    outputWidth = maxDimension;
                    outputHeight = Math.round(outputHeight * scale);
                }
                if (outputHeight > maxDimension) {
                    const scale = maxDimension / outputHeight;
                    outputHeight = maxDimension;
                    outputWidth = Math.round(outputWidth * scale);
                }

                const canvas = document.createElement('canvas');
                canvas.width = outputWidth;
                canvas.height = outputHeight;
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    reject(new Error('Canvas context not found'));
                    return;
                }

                // Fill white background in case of out-of-bound crop area
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, outputWidth, outputHeight);

                // Draw cropped portion directly onto scaled canvas
                ctx.drawImage(
                    image,
                    roundedX,
                    roundedY,
                    roundedWidth,
                    roundedHeight,
                    0,
                    0,
                    outputWidth,
                    outputHeight
                );

                // Export as JPEG with 0.85 quality for compact file size (~60KB - 150KB)
                canvas.toBlob(
                    (blob) => resolve(blob),
                    'image/jpeg',
                    0.85
                );
            } catch (err) {
                reject(err);
            }
        };
        image.onerror = reject;
    });
};

interface ImageCropperProps {
    imageSrc: string;
    onApply: (croppedFile: File) => void;
    onClose: () => void;
    aspect?: number;
}

export const ImageCropper: React.FC<ImageCropperProps> = ({ imageSrc, onApply, onClose, aspect = 1 }) => {
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

    const onCropComplete = useCallback((croppedArea: any, croppedAreaPixels: any) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const handleApply = async () => {
        if (croppedAreaPixels) {
            const blob = await getCroppedImg(imageSrc, croppedAreaPixels, aspect);
            if (blob) {
                onApply(new File([blob], 'cropped_image.jpg', { type: 'image/jpeg' }));
            }
        }
    };

    return (
        <div className="fixed inset-0 bg-slate-900/80 z-[9999] flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl w-full max-w-2xl overflow-hidden shadow-2xl flex flex-col h-[650px] animate-fade-in">
                <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                    <h3 className="text-xl font-bold text-slate-800">Adjust Picture</h3>
                    <span className="text-xs font-bold text-slate-400 bg-slate-50 px-2.5 py-1 rounded-full uppercase tracking-wider">Drag to position, scroll to zoom</span>
                </div>
                <div className="relative flex-grow bg-slate-100/50">
                    <Cropper
                        image={imageSrc}
                        crop={crop}
                        zoom={zoom}
                        aspect={aspect}
                        onCropChange={setCrop}
                        onCropComplete={onCropComplete}
                        onZoomChange={setZoom}
                        minZoom={0.1}
                        maxZoom={3}
                        restrictPosition={false}
                    />
                </div>
                <div className="p-6 border-t border-slate-100 flex flex-col sm:flex-row justify-between items-center gap-4 bg-slate-50/50">
                    <div className="flex items-center space-x-3 text-slate-500 w-full sm:w-auto justify-between sm:justify-start">
                        <ZoomOut 
                            size={18} 
                            className="cursor-pointer hover:text-[#0094c8] transition-colors shrink-0" 
                            onClick={() => setZoom(prev => Math.max(0.1, prev - 0.1))} 
                        />
                        <input 
                            type="range"
                            value={zoom}
                            min={0.1}
                            max={3}
                            step={0.05}
                            onChange={(e) => setZoom(parseFloat(e.target.value))}
                            className="w-full sm:w-48 cursor-pointer accent-[#0094c8] h-1.5 bg-slate-200 rounded-lg appearance-none"
                        />
                        <ZoomIn 
                            size={18} 
                            className="cursor-pointer hover:text-[#0094c8] transition-colors shrink-0" 
                            onClick={() => setZoom(prev => Math.min(3, prev + 0.1))} 
                        />
                        <span className="text-xs font-black text-slate-600 bg-white px-2 py-1 rounded-md border border-slate-200 w-12 text-center shrink-0">{Math.round(zoom * 100)}%</span>
                    </div>
                    <div className="space-x-2 flex items-center w-full sm:w-auto justify-end">
                        <button onClick={onClose} className="px-5 py-2.5 font-bold text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-all text-xs uppercase tracking-wider">Cancel</button>
                        <button onClick={handleApply} className="px-5 py-2.5 bg-[#0094c8] text-white rounded-xl font-bold hover:bg-[#007ba6] text-xs uppercase tracking-wider shadow-sm transition-all hover:scale-[1.02] active:scale-[0.98]">Apply</button>
                    </div>
                </div>
            </div>
        </div>
    );
};
